import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CloseRounded } from '@mui/icons-material';

import WizardDataContext from '../store/WizardDataContext';
import { WIZARD_STEPS, getStepByIndex, getTotalSteps } from '../config/wizardSteps';
import { saveStepData, createInitialCoupon, savePopinLocationsFromWizard, updatePopinBudget } from '../utils/wizardApi';
import { COUPON_TOKEN_INVALID_MESSAGE, POPIN_TOKEN_INVALID_MESSAGE } from '../utils/couponApiUtils';
import WizardNavigation from '../Components/wizard/WizardNavigation';
import WizardErrorBoundary from '../Components/wizard/WizardErrorBoundary';
import { usePaymentLockGuard } from '../hooks/usePaymentLockGuard';

// Import step components
import CompanyDetails from '../Components/advertise/coupon/CompanyDetails';
import LogoUpload from '../Components/advertise/coupon/LogoUpload';
import ChooseDeals from '../Components/advertise/coupon/ChooseDeals';
import CouponType from '../Components/advertise/coupon/CouponType';
import CouponExpiration from '../Components/advertise/coupon/CouponExpiration';
import CouponCodeAndDetails from '../Components/advertise/coupon/CouponCodeAndDetails';
import TermsAndConditions from '../Components/advertise/coupon/TermsAndConditions';
import Instructions from '../Components/advertise/coupon/Instructions';
import Locations from '../Components/advertise/coupon/Locations';
import Categories from '../Components/advertise/coupon/Categories';

// Component mapping
const stepComponents = {
  CompanyDetails,
  LogoUpload,
  ChooseDeals,
  CouponType,
  CouponExpiration,
  CouponCodeAndDetails,
  TermsAndConditions,
  Instructions,
  Locations,
  Categories
};

const WizardContainer = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { term, id: stepIndexParam } = params;
  
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;

  const isPopin = wizardData.dealSelection?.selectedDeal === 'popin';
  const includeCoupon = wizardData.dealSelection?.includeCoupon ?? true;
  const isPopinWithoutCoupon = isPopin && includeCoupon === false;

  // Once an entry is paid it can't be edited again — Back/bfcache into the wizard
  // sends the user to the success page instead of letting them change locations etc.
  usePaymentLockGuard({ couponId: wizardData.meta.couponId, popinId: wizardData.meta.popinId });

  const [activeStepIndex, setActiveStepIndex] = useState(null);
  const [disableBtn, setDisableBtn] = useState(true);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [isDataEditing, setIsDataEditing] = useState(false);
  const [couponId, setCouponId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentCouponId, setCurrentCouponId] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [returnTo, setReturnTo] = useState(null);
  const hasInitialized = useRef(false);

  // Get current step config and next step (for "NEXT STEP" label)
  // Use step index from URL when available so "NEXT STEP" title is always correct
  const stepNum = activeStepIndex != null ? Number(activeStepIndex) : (stepIndexParam ? parseInt(stepIndexParam, 10) : null);
  const currentStep = stepNum ? getStepByIndex(stepNum) : null;
  const nextStep = stepNum != null ? getStepByIndex(stepNum + 1) : null;
  const totalSteps = getTotalSteps();

  const buildStepUrl = (stepIndex) => {
    const couponPart = currentCouponId || wizardData.meta.couponId;
    const popinPart = wizardData.meta.popinId ? `&popinId=${wizardData.meta.popinId}` : '';
    return `/advertise/coupon/${term}/create-coupon/${stepIndex}?edit=false&companyId=${wizardData.meta.companyId}&couponId=${couponPart}${popinPart}`;
  };

  const goToStep = (stepIndex) => {
    navigate(buildStepUrl(stepIndex));
  };

  // Initialize step index from URL
  useEffect(() => {
    if (stepIndexParam) {
      const stepIndex = parseInt(stepIndexParam);
      setActiveStepIndex(stepIndex);
    }
  }, [stepIndexParam]);

  // Scroll to top whenever the user moves to a new step/page (scroll the main container .App, not window)
  useEffect(() => {
    if (activeStepIndex != null) {
      const scrollEl = document.querySelector('.App');
      if (scrollEl) scrollEl.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    }
  }, [activeStepIndex]);

  // Guard: prevent invalid deep links for Pop-In routing
  useEffect(() => {
    if (!activeStepIndex) return;

    // Pop-In WITHOUT coupon skips categories (step 10). If a coupon is included,
    // categories should behave like normal coupon flow.
    if (isPopinWithoutCoupon && activeStepIndex === 10) {
      const pid = wizardData.meta.popinId;
      navigate(
        `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${currentCouponId || wizardData.meta.couponId}${pid ? `&popinId=${pid}` : ''}`,
        { replace: true }
      );
      return;
    }

    // Pop-In with no coupon skips coupon steps. If the user deep-links into a
    // skipped step, send them to Budget (5) when budget isn't chosen yet so
    // step 9 doesn't fail on "Missing pop-in budget selection"; otherwise to
    // Locations (9).
    if (isPopinWithoutCoupon && [4, 6, 7, 8].includes(activeStepIndex)) {
      goToStep(wizardData.popinBudget?.popinRateId ? 9 : 5);
    }
  }, [activeStepIndex, isPopin, isPopinWithoutCoupon, wizardData.meta.companyId, wizardData.meta.couponId, wizardData.popinBudget?.popinRateId, currentCouponId, navigate]);

  // Parse query params for edit mode
  useEffect(() => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    const newIsDataEditing = query.get('edit') === 'true';
    setIsDataEditing(newIsDataEditing);
    setCouponId(+query.get('couponId'));
    setReturnTo(query.get('returnTo'));
    
    if (!newIsDataEditing) {
      setDataLoaded(false);
    }
    
    if (query.get('companyId')) {
      updateData('meta', { companyId: +query.get('companyId') });
    }
    if (query.get('couponId')) {
      updateData('meta', { couponId: +query.get('couponId') });
    }
    if (query.get('popinId')) {
      updateData('meta', { popinId: +query.get('popinId') });
    }
  }, []);

  // Clear data when starting fresh (step 1, edit=false, no companyId/couponId)
  useEffect(() => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    const isEditing = query.get('edit') === 'true';
    const hasCompanyIdInUrl = query.get('companyId');
    const hasCouponIdInUrl = query.get('couponId');
    const currentStep = parseInt(stepIndexParam);
    
    if (!hasInitialized.current && currentStep === 1 && !isEditing && !hasCompanyIdInUrl && !hasCouponIdInUrl) {
      wizardCtx.clearAllData();
      hasInitialized.current = true;
    } else if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [stepIndexParam, wizardCtx]);

  useEffect(() => {
    if (isDataEditing && couponId && !dataLoaded) {
      setDataLoaded(true);
    }
  }, [isDataEditing, couponId, dataLoaded]);

  // Control Save & Continue button state
  useEffect(() => {
    if (isDataEditing) {
      setDisableSaveBtn(disableBtn);
    } else {
      setDisableSaveBtn(true);
    }
  }, [isDataEditing, disableBtn]);

  // Handle next step navigation
  const handleNext = async () => {
    if (!currentStep) return;

    let stepUpdateSuccess = true;
    let resolvedPopinId = wizardData.meta.popinId;

    // Save step data if API config exists
    if (currentStep.apiConfig) {
      // Pop-In reuses step 5 (expiration) as Budget; don't PATCH /cost with empty
      // duration payload. If a pop-in draft already exists (e.g. the user came
      // back to edit budget), push the new rate so price/visitors stay in sync.
      if (isPopin && currentStep.id === 'expiration') {
        const result = await updatePopinBudget({
          popinId: wizardData.meta.popinId,
          popinCreatorToken: wizardData.meta.popinCreatorToken,
          popinRateId: wizardData.popinBudget?.popinRateId
        });
        stepUpdateSuccess = result.success;
        if (result.tokenInvalid) {
          wizardCtx.clearAllData();
          alert(result.message || POPIN_TOKEN_INVALID_MESSAGE);
          navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
          return;
        }
      } else if (isPopin && currentStep.id === 'locations') {
        const result = await savePopinLocationsFromWizard(wizardData, {
          companyId: wizardData.meta.companyId,
          couponId: wizardData.meta.couponId,
          popinId: wizardData.meta.popinId,
          popinCreatorToken: wizardData.meta.popinCreatorToken
        });
        stepUpdateSuccess = result.success;
        if (result.tokenInvalid) {
          wizardCtx.clearAllData();
          alert(result.message || POPIN_TOKEN_INVALID_MESSAGE);
          navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
          return;
        }
        if (result.success && result.popinId && result.popinCreatorToken) {
          updateData('meta', { popinId: result.popinId, popinCreatorToken: result.popinCreatorToken });
          resolvedPopinId = result.popinId;
        }
      } else {
        const couponIdToUse = currentCouponId || wizardData.meta.couponId;
        const creatorToken = wizardData.meta.creatorToken;
        if (couponIdToUse) {
          const result = await saveStepData(currentStep, wizardData, couponIdToUse, creatorToken);
          stepUpdateSuccess = result.success;
          if (result.tokenInvalid) {
            wizardCtx.clearAllData();
            alert(result.message || COUPON_TOKEN_INVALID_MESSAGE);
            navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
            return;
          }
        }
      }
    } else {
      stepUpdateSuccess = true; // No API call needed
    }

    if (stepUpdateSuccess) {
      // Pop-In routing overrides
      // Step 3 (deal selection): if Pop-In with no coupon, jump to budget (step 5)
      if (activeStepIndex === 3 && isPopinWithoutCoupon) {
        goToStep(5);
        return;
      }
      if (activeStepIndex === 5 && isPopin) {
        // Step 5 is Budget for Pop-In (reuses CouponExpiration)
        if (isPopinWithoutCoupon) goToStep(9);
        else goToStep(6);
        return;
      }
      if (activeStepIndex === 9 && isPopin) {
        // After locations:
        // - Pop-In without coupon: jump to review/payment (resume)
        // - Pop-In with coupon: continue to categories (step 10) like normal flow
        if (isPopinWithoutCoupon) {
          const pid = resolvedPopinId;
          navigate(
            `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${currentCouponId || wizardData.meta.couponId}${pid ? `&popinId=${pid}` : ''}`
          );
          return;
        }
      }

      const nextStepIndex = activeStepIndex + 1;
      if (nextStepIndex <= totalSteps) goToStep(nextStepIndex);
      else {
        const pid = resolvedPopinId;
        navigate(
          `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${currentCouponId || wizardData.meta.couponId}${pid ? `&popinId=${pid}` : ''}`
        );
      }
    } else {
      console.error('Failed to update step data');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (activeStepIndex > 1) {
      // Pop-In routing overrides
      if (activeStepIndex === 5 && isPopin && isPopinWithoutCoupon) {
        goToStep(3);
        return;
      }
      if (activeStepIndex === 9 && isPopinWithoutCoupon) {
        goToStep(5);
        return;
      }

      const prevStepIndex = activeStepIndex - 1;
      goToStep(prevStepIndex);
    }
  };

  // Handle next click with special logic for steps 1, 2, and 10
  const handleNextClick = async (e) => {
    e.preventDefault();

    if (activeStepIndex === 1) {
      // Step 1: Company Details. We no longer create the coupon here — coupon
      // creation is deferred to step 3 (after deal selection) so pop-in-without-
      // coupon flows never leave an orphan draft coupon behind.
      setLoading(true);
      try {
        const existingCompanyId = wizardData.company.companyId || wizardData.meta.companyId;

        if (existingCompanyId) {
          // Existing company — save contact details, then skip straight to deal selection (step 3).
          try {
            const contactPayload = {
              contact: wizardData.company.managerName,
              email: wizardData.company.email,
              mobile: wizardData.company.managerNumber
            };

            const contactRes = await fetch(`${process.env.REACT_APP_API_URI}/v3/company/${existingCompanyId}/contact`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(contactPayload)
            });

            const contactData = await contactRes.json();
            if (contactData.status === 'success') {
              console.log('Contact details saved successfully');
            }
          } catch (error) {
            console.error('Error saving contact details:', error);
          }

          updateData('meta', { companyId: existingCompanyId });
          navigate(`/advertise/coupon/${term}/create-coupon/3?edit=false&companyId=${existingCompanyId}`);
        } else {
          // New company — create company, then go to logo upload (step 2).
          if (wizardData.meta.companyId) {
            navigate(`/advertise/coupon/${term}/create-coupon/2?edit=false&companyId=${wizardData.meta.companyId}`);
          } else {
            const companyResult = await wizardCtx.createCompanyDraft();
            if (companyResult.success) {
              updateData('meta', { companyId: companyResult.companyId });
              navigate(`/advertise/coupon/${term}/create-coupon/2?edit=false&companyId=${companyResult.companyId}`);
            }
          }
        }
      } catch (error) {
        console.error('Error handling company:', error);
      } finally {
        setLoading(false);
      }
    } else if (activeStepIndex === 2) {
      // Step 2: Logo Upload — logo is already uploaded against companyId by the
      // component itself, so just advance to deal selection. Coupon creation is
      // deferred to step 3.
      navigate(`/advertise/coupon/${term}/create-coupon/3?edit=false&companyId=${wizardData.meta.companyId}`);
    } else if (activeStepIndex === 3) {
      // Step 3: Deal Selection — this is the first point we know whether a coupon
      // is actually needed. Create the draft coupon now (with source='popin_deal'
      // when it will be bundled with a pop-in) so pop-in-without-coupon flows
      // never insert an orphan coupons row.
      setLoading(true);
      try {
        const selectedDeal = wizardData.dealSelection?.selectedDeal;
        const includeCouponForDeal = wizardData.dealSelection?.includeCoupon !== false;
        const needsCoupon = selectedDeal !== 'popin' || includeCouponForDeal;
        const alreadyHaveCoupon = !!(currentCouponId || wizardData.meta.couponId);

        let couponIdForUrl = currentCouponId || wizardData.meta.couponId || null;

        if (needsCoupon && !alreadyHaveCoupon) {
          const source = selectedDeal === 'popin' ? 'popin_deal' : null;
          const result = await createInitialCoupon(wizardData.meta.companyId, wizardData, source);
          if (!result.success) {
            console.error('Failed to create initial coupon:', result.error);
            return;
          }
          setCurrentCouponId(result.couponId);
          updateData('meta', { couponId: result.couponId, creatorToken: result.creatorToken ?? null });
          couponIdForUrl = result.couponId;
        }

        // Route from step 3:
        //  - pop-in without coupon  → Budget (step 5)
        //  - anything else          → Coupon Type (step 4)
        const nextIndex = selectedDeal === 'popin' && !includeCouponForDeal ? 5 : 4;
        const couponQs = couponIdForUrl ? `&couponId=${couponIdForUrl}` : '';
        navigate(
          `/advertise/coupon/${term}/create-coupon/${nextIndex}?edit=false&companyId=${wizardData.meta.companyId}${couponQs}`
        );
      } catch (error) {
        console.error('Error during deal selection transition:', error);
      } finally {
        setLoading(false);
      }
    } else if (activeStepIndex === 10) {
      // Step 10: Categories - save and go to resume
      const result = await saveStepData(currentStep, wizardData, currentCouponId || wizardData.meta.couponId, wizardData.meta.creatorToken);
      if (result.tokenInvalid) {
        wizardCtx.clearAllData();
        alert(result.message || 'This coupon link is invalid or expired. Please start again from the beginning.');
        navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
        return;
      }
      if (result.success) {
        const pid = wizardData.meta.popinId;
        navigate(
          `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${currentCouponId || wizardData.meta.couponId}${pid ? `&popinId=${pid}` : ''}`
        );
      } else {
        console.error('Failed to save categories');
      }
    } else {
      // Steps 3-9: Normal next flow
      await handleNext();
    }
  };

  // Handle save and continue (edit mode)
  const continueToResumePage = async () => {
    setLoading(true);
    try {
      let updateSuccess = true;
      let resolvedPopinId = wizardData.meta.popinId;

      if (currentStep && currentStep.apiConfig) {
        // Pop-In reuses step 5 (expiration) as Budget. When a pop-in draft already
        // exists (user re-editing budget from the resume page), persist the rate
        // change so the popin's price/visitors — and the PayFast link — stay in sync.
        if (isPopin && currentStep.id === 'expiration') {
          const result = await updatePopinBudget({
            popinId: wizardData.meta.popinId,
            popinCreatorToken: wizardData.meta.popinCreatorToken,
            popinRateId: wizardData.popinBudget?.popinRateId
          });
          updateSuccess = result.success;
          if (result.tokenInvalid) {
            wizardCtx.clearAllData();
            alert(result.message || POPIN_TOKEN_INVALID_MESSAGE);
            navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
            setLoading(false);
            return;
          }
        } else if (isPopin && currentStep.id === 'locations') {
          const result = await savePopinLocationsFromWizard(wizardData, {
            companyId: wizardData.meta.companyId,
            couponId: wizardData.meta.couponId,
            popinId: wizardData.meta.popinId,
            popinCreatorToken: wizardData.meta.popinCreatorToken
          });
          updateSuccess = result.success;
          if (result.tokenInvalid) {
            wizardCtx.clearAllData();
            alert(result.message || POPIN_TOKEN_INVALID_MESSAGE);
            navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
            setLoading(false);
            return;
          }
          if (result.success && result.popinId && result.popinCreatorToken) {
            updateData('meta', { popinId: result.popinId, popinCreatorToken: result.popinCreatorToken });
            resolvedPopinId = result.popinId;
          }
        } else {
          const couponIdToUse = currentCouponId || wizardData.meta.couponId;
          if (couponIdToUse) {
            const result = await saveStepData(currentStep, wizardData, couponIdToUse, wizardData.meta.creatorToken);
            updateSuccess = result.success;
            if (result.tokenInvalid) {
              wizardCtx.clearAllData();
              alert(result.message || COUPON_TOKEN_INVALID_MESSAGE);
              navigate(`/advertise/coupon/${term}/create-coupon/1?edit=false`);
              setLoading(false);
              return;
            }
          }
        }
      }

      if (updateSuccess) {
        const pid = resolvedPopinId;
        if (returnTo) {
          navigate(returnTo);
        } else {
          navigate(
            `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${wizardData.meta.couponId || currentCouponId}${pid ? `&popinId=${pid}` : ''}`
          );
        }
      } else {
        console.error('Failed to update step data');
      }
    } catch (error) {
      console.error('Error in continueToResumePage:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle close button
  const handleClose = () => {
    if (isDataEditing) {
      const pid = wizardData.meta.popinId;
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(
          `/advertise/coupon/resume?companyId=${wizardData.meta.companyId}&couponId=${wizardData.meta.couponId || currentCouponId}${pid ? `&popinId=${pid}` : ''}`
        );
      }
    } else {
      if (activeStepIndex === 1) {
        navigate('/advertise/coupon/help');
      }
    }
  };

  // Render current step component with error boundary
  const renderStepComponent = () => {
    if (!currentStep || !activeStepIndex) return null;

    const Component = stepComponents[currentStep.component];
    if (!Component) {
      console.error(`Component ${currentStep.component} not found`);
      return null;
    }

    // Common props for all components
    const commonProps = {
      setDisableBtn: setDisableBtn
    };

    // Wrap component in error boundary
    const renderComponent = () => {
      if (activeStepIndex === 1) {
        return <Component {...commonProps} pushData={false} />;
      } else if (activeStepIndex === 2) {
        return <Component {...commonProps} companyId={wizardData.meta.companyId} />;
      } else if ([4, 5, 6, 7, 8, 9, 10].includes(activeStepIndex)) {
        return <Component {...commonProps} companyId={wizardData.meta.companyId} couponId={currentCouponId || wizardData.meta.couponId} />;
      } else {
        return <Component {...commonProps} />;
      }
    };

    return (
      <WizardErrorBoundary onGoBack={handleBack}>
        {renderComponent()}
      </WizardErrorBoundary>
    );
  };

  if (!activeStepIndex || !currentStep) {
    return <div>Loading...</div>;
  }

  // Compute dynamic "Next step" label
  const displayNextStep = (() => {
    if (!activeStepIndex) return nextStep;

    // Categories is always the last wizard step → Review is what follows.
    if (activeStepIndex === 10) return { title: 'Review' };

    if (isPopin) {
      if (activeStepIndex === 3) return includeCoupon ? getStepByIndex(4) : getStepByIndex(5);
      if (activeStepIndex === 4) return getStepByIndex(5);
      if (activeStepIndex === 5) return includeCoupon ? getStepByIndex(6) : getStepByIndex(9);
      // After Locations: Pop-In WITHOUT coupon jumps to Review;
      // Pop-In WITH coupon continues to Categories (like the normal flow).
      if (activeStepIndex === 9) return isPopinWithoutCoupon ? { title: 'Review' } : getStepByIndex(10);
    }

    return nextStep;
  })();

  return (
    <>
      <div className="steps">
        {renderStepComponent()}
      </div>
      
      <WizardNavigation
        currentStep={currentStep}
        nextStep={displayNextStep}
        currentStepIndex={activeStepIndex}
        totalSteps={totalSteps}
        onBack={activeStepIndex === 1 ? handleClose : handleBack}
        onNext={handleNextClick}
        onSaveAndContinue={continueToResumePage}
        disableBtn={disableBtn}
        disableSaveBtn={disableSaveBtn}
        loading={loading}
        isDataEditing={isDataEditing}
      />
      
      <div className="deliva-logo">
        <div className="logo"></div>
        {(isDataEditing || activeStepIndex === 1) && (
          <div className="close-coupon" onClick={handleClose}>
            <CloseRounded />
          </div>
        )}
      </div>
    </>
  );
};

export default WizardContainer;
